import React, { useEffect, useState } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import InputField from '@/UIlibrary/InputField/InputField';
import Checkbox from '@/UIlibrary/Checkbox/Checkbox';
import { RootQueryType } from '@/gql/graphql';
import { gql, useQuery } from '@apollo/client';
import { useTranslations } from 'use-intl';
import { Search } from 'react-feather';

interface DashboardCardProps {
	name: string;
	amount: number;
	description: string;
	from: string;
	onClick?: () => void;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
	name,
	amount = 0,
	description,
	onClick = () => {},
	from
}) => {
	return (
		<div
			className="flex flex-col bg-white rounded-lg shadow-md p-4 md:p-6 gap-4 hover:bg-slate-100 cursor-pointer w-1/5"
			onClick={onClick}
		>
			<div className="flex flex-col grow gap-8">
				<Typography size="xl" weight="semibold">
					{name}
				</Typography>
				<Typography size="xl" weight="semibold">
					{amount}
				</Typography>
				<Typography>{description}</Typography>
			</div>
			<div className="flex flex-col items-start">
				<Typography size="sm" color="subtitle">
					{from}
				</Typography>
			</div>
		</div>
	);
};

const GET_REGISTRIES = gql`
	query GetRegistries {
		listRegistries {
			count
		}
	}
`;

const DashboardContent: React.FC = () => {
	const [searchInput, setSearchInput] = useState('');
	const [metrics, setMetrics] = useState<DashboardCardProps[]>([]);
	const [filteredMetrics, setFilteredMetrics] = useState<DashboardCardProps[]>([]);
	const { loading, data } = useQuery<RootQueryType>(GET_REGISTRIES);
	const t = useTranslations('Dashboard');

	const cards: DashboardCardProps[] = [
		{
			name: t('TotalTransactions'),
			amount: 0,
			description: t('TotalTransactionsDescription'),
			from: t('ThisWeek')
		},
		{
			name: t('TotalClients'),
			amount: 0,
			description: t('TotalClientsDescription'),
			from: t('FromStart')
		},
		{
			name: t('TotalActiveClients'),
			amount: 0,
			description: t('TotalActiveClientsDescription'),
			from: t('ThisWeek')
		},
		{
			name: t('NewRegistrations'),
			amount: 0,
			description: t('NewRegistrationsDescription'),
			from: t('ThisWeek')
		},
		{
			name: t('ActiveLoanPortfolio'),
			amount: 0,
			description: t('ActiveLoanPortfolio'),
			from: t('ActiveLoans')
		},
		{
			name: t('GoodStandingLoans'),
			amount: 0,
			description: t('GoodStandingLoansDescription'),
			from: t('ActiveLoans')
		},
		{
			name: t('ActiveDelinquentLoans'),
			amount: 0,
			description: t('ActiveDelinquentLoansDescription'),
			from: t('ActiveLoans')
		},
		{
			name: t('TotalDelinquentLoans'),
			amount: 0,
			description: t('TotalDelinquentLoansDescription'),
			from: t('Cumulative')
		}
	];

	useEffect(() => {
		if (data && data.listRegistries) {
			if (data?.listRegistries.count !== undefined) {
				const updatedCards = [...cards];
				updatedCards[1].amount = data.listRegistries.count || 0;
				setMetrics(updatedCards);
			}
		}
		setMetrics(cards);
		setFilteredMetrics(cards);
	}, [data]);

	const getSearchableFields = (metric: DashboardCardProps) => {
		return {
			amount: metric.amount,
			name: metric.name,
			description: metric.description,
			from: metric.from
		};
	};

	const searchInFields = (
		searchableFields: Record<string, string | number | undefined>,
		searchTerm: string
	) => {
		return Object.values(searchableFields)
			.filter(Boolean)
			.some((field) => field && field.toString().toLowerCase().includes(searchTerm.toLowerCase()));
	};

	useEffect(() => {
		const filterMetrics = metrics.filter((metric) => {
			const fields = searchInFields(getSearchableFields(metric), searchInput);
			return fields;
		});
		setFilteredMetrics(filterMetrics);
	}, [metrics, searchInput]);

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	return (
		<div className="flex flex-col mt-2 py-4 gap-6">
			<div className="flex gap-3">
				<Checkbox onChange={() => {}} name="hello" checked={false} label="Finclu" />
				<Checkbox onChange={() => {}} name="hello" checked={false} label="Finclu Empresas" />
			</div>
			<div className="flex flex-col md:flex-row w-full justify-between items-center">
				<div className="inline-flex flex-row items-center gap-1">
					<div className="w-full">
						<InputField
							onChange={(e) => {
								setSearchInput(e.target.value);
							}}
							shape="pill"
							placeholder={t('Search')}
							dataTestId={'searchInput'}
							startAction={<Search size={20} />}
						/>
					</div>
				</div>
				<div className="hidden md:block mx-auto w-full md:w-auto my-4 md:my-0 md:mx-0">
					{/* <Button label={t('AskAI')} size={'auto'} onClick={() => {}} disabled /> */}
				</div>
			</div>

			<div className="flex flex-row flex-wrap w-full gap-8 justify-center ">
				{filteredMetrics.map((metric, index) => (
					<DashboardCard
						key={index}
						name={metric.name}
						amount={metric.amount}
						description={metric.description}
						from={metric.from}
					/>
				))}
			</div>
		</div>
	);
};

export default DashboardContent;
